import { useState } from "preact/hooks";
import { tr } from "../i18n.js";
import { fmtMoney, get, html, patch, post } from "../lib.js";
import { Badge, Field, Modal, toast, useAction, useForm, useLoad } from "../ui.js";

/** Where a tracked link lives. Keys match LINK_SOURCES in src/crm/links.ts; also used as lead-source labels. */
export const LINK_SOURCE_LABEL = {
  qr_code: "QR code / sticker",
  instagram_bio: "Instagram bio",
  facebook_page: "Facebook page",
  google_business: "Google Business profile",
  website: "Website button",
  flyer: "Flyer / print",
  ad: "Ad",
  other: "Other",
};

const copy = (text) => navigator.clipboard.writeText(text).then(() => toast(tr("Copied")));

/**
 * Tracked WhatsApp links and QR codes: one per place the business puts its number. Each opens a
 * short link that counts the open and forwards to WhatsApp with a "(ref CODE)" tag, so the chats it
 * starts are credited to it, with their bookings and revenue.
 */
export function TrackedLinks({ t }) {
  const [data, reload] = useLoad(() => get(`${t.base}/links`), [t.id]);
  const [editing, setEditing] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [busy, run] = useAction();
  if (!data) return null;
  const canEdit = t.role !== "agent";
  const links = data.links.filter((l) => showArchived || !l.archived_at);
  const archivedCount = data.links.filter((l) => l.archived_at).length;
  const currency = "ILS";

  const archive = async (l) => {
    await run(() => patch(`${t.base}/links/${l.id}`, { archived: !l.archived_at }), l.archived_at ? tr("Link restored") : tr("Link archived"));
    reload();
  };

  return html`<section class="card">
    <div class="section-head"><h2>${tr("WhatsApp links and QR codes")}</h2>
      ${canEdit && html`<button class="btn small primary" onClick=${() => setEditing({})}>${tr("New link")}</button>`}</div>
    <p class="muted small">${tr("Make one link per place your number appears: the shop window, your Instagram bio, a flyer, an ad. Each opens WhatsApp with a first message tagged with its code, so you see which one brings chats, bookings and revenue.")}</p>
    ${!links.length
      ? html`<p class="muted small">${tr("No links yet.")}</p>`
      : html`<div class="link-list">${links.map((l) => html`<article class="link-item ${l.archived_at ? "archived" : ""}">
          <img class="link-qr" src=${`/api${t.base}/links/${l.id}/qr.svg`} alt=${tr("QR code for {name}", { name: l.name })} loading="lazy" />
          <div class="link-body">
            <div class="section-head"><strong dir="auto">${l.name}</strong>
              <span>${l.archived_at ? html`<${Badge} kind="muted">${tr("Archived")}</${Badge}>` : html`<${Badge} kind="neutral">${tr(LINK_SOURCE_LABEL[l.source] ?? l.source)}</${Badge}>`}</span></div>
            <code class="small link-url" dir="ltr">${l.short_url}</code>
            <div class="muted small" dir="auto">${l.prefilled}</div>
            <div class="link-stats small">
              <span><strong>${l.clicks}</strong> ${tr("opens")}</span>
              <span><strong>${l.leads}</strong> ${tr("chats")}</span>
              <span><strong>${l.booked}</strong> ${tr("with a booking")}</span>
              <span><strong>${l.revenue ? fmtMoney(l.revenue, currency) : "—"}</strong> ${tr("revenue")}</span>
            </div>
            <div class="form-actions">
              <button class="btn small" onClick=${() => copy(l.short_url)}>${tr("Copy link")}</button>
              <a class="btn small" href=${`/api${t.base}/links/${l.id}/qr.svg`} download=${`whatsapp-${l.code}.svg`}>${tr("Download QR")}</a>
              ${canEdit && html`<button class="btn small ghost" onClick=${() => setEditing(l)}>${tr("Edit")}</button>
                <button class="btn small ghost" disabled=${busy} onClick=${() => archive(l)}>${l.archived_at ? tr("Restore") : tr("Archive")}</button>`}
            </div>
          </div>
        </article>`)}</div>`}
    ${archivedCount > 0 && html`<button class="link" onClick=${() => setShowArchived(!showArchived)}>${showArchived ? tr("Hide archived") : tr("Show {n} archived", { n: archivedCount })}</button>`}
    <p class="muted small">${tr("Opens include link previews, so they run a little higher than real visits. A customer who deletes the code before sending arrives as Direct.")}</p>
    ${editing && html`<${LinkForm} t=${t} link=${editing} defaultPhone=${data.default_phone} sources=${data.sources} onClose=${() => setEditing(null)} onSaved=${() => { setEditing(null); reload(); }} />`}
  </section>`;
}

function LinkForm({ t, link, defaultPhone, sources, onClose, onSaved }) {
  const isNew = !link.id;
  const { values, bind } = useForm({
    name: link.name ?? "",
    source: link.source ?? "qr_code",
    phone: link.phone ? `+${link.phone}` : defaultPhone ? `+${defaultPhone}` : "",
    message: link.message ?? tr("Hi! I'd like to book an appointment"),
  });
  const [busy, run] = useAction();
  const save = async (e) => {
    e.preventDefault();
    await run(() => (isNew ? post(`${t.base}/links`, values) : patch(`${t.base}/links/${link.id}`, values)), isNew ? tr("Link created") : tr("Saved"));
    onSaved();
  };
  return html`<${Modal} title=${isNew ? tr("New WhatsApp link") : tr("Edit link")} onClose=${onClose}>
    <form onSubmit=${save}>
      <${Field} label=${tr("Name")} hint=${tr("Where it goes, e.g. Window sticker. Shown as the campaign in lead sources.")}><input required maxlength="80" ...${bind("name")} /></${Field}>
      <${Field} label=${tr("Placed on")}><select ...${bind("source")}>${sources.map((s) => html`<option value=${s}>${tr(LINK_SOURCE_LABEL[s] ?? s)}</option>`)}</select></${Field}>
      <${Field} label=${tr("WhatsApp number")} hint=${tr("With the country code. Filled in from your connected number when we can read it.")}><input required dir="ltr" inputmode="tel" ...${bind("phone")} /></${Field}>
      <${Field} label=${tr("First message")} hint=${tr("What the customer sees already typed. The code is added at the end automatically.")}><textarea required rows="3" maxlength="300" dir="auto" ...${bind("message")}></textarea></${Field}>
      ${!isNew && html`<p class="muted small">${tr("The link and QR code stay the same when you edit, so anything already printed keeps working.")}</p>`}
      <div class="form-actions"><button class="btn primary" disabled=${busy}>${isNew ? tr("Create link") : tr("Save")}</button><button type="button" class="btn ghost" onClick=${onClose}>${tr("Cancel")}</button></div>
    </form>
  </${Modal}>`;
}
